import { useLayoutEffect } from 'react';

/**
 * The modality of a DetailDock that has become a full-screen sheet. While `covered` is true everything behind the
 * dock (every sibling along the way up to the app root; #root's own siblings, such as toasts, stay live) is inert
 * and aria-hidden, and the page does not scroll under it. A layout effect, so it is all live again before focus
 * returns to the row. `getDock` finds the dock element once the effect runs.
 */
export default function useSheetModal(covered: boolean, getDock: () => HTMLElement | null): void {
  useLayoutEffect(() => {
    if (!covered) return undefined;
    const dockEl = getDock();
    if (!dockEl || typeof document === 'undefined') return undefined;
    const made: Array<{ el: HTMLElement; priorAriaHidden: string | null }> = [];
    let node: HTMLElement = dockEl;
    while (node.parentElement && node.parentElement !== document.body && node.parentElement !== document.documentElement) {
      for (const sib of Array.from(node.parentElement.children)) {
        if (sib === node || !(sib instanceof HTMLElement) || /^(SCRIPT|STYLE|TEMPLATE)$/.test(sib.tagName)) continue;
        if (sib.hasAttribute('inert')) continue;
        // Remember what was there: a sibling keeps its own aria-hidden ("true" or "false") when the sheet closes.
        made.push({ el: sib, priorAriaHidden: sib.getAttribute('aria-hidden') });
        sib.setAttribute('inert', '');
        sib.setAttribute('aria-hidden', 'true');
      }
      node = node.parentElement;
    }
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => {
      for (const m of made) {
        m.el.removeAttribute('inert');
        if (m.priorAriaHidden === null) m.el.removeAttribute('aria-hidden');
        else m.el.setAttribute('aria-hidden', m.priorAriaHidden);
      }
      html.style.overflow = prevOverflow;
    };
    // getDock is a ref reader; the effect is keyed on the sheet opening and closing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [covered]);
}
